/**
 * Formatting Utilities
 * Various formatting functions for form data
 */

export class FormatUtils {
  /**
   * Escape HTML to prevent XSS. Quotes are escaped too so the result is safe inside attribute values.
   * @param {string} text - Text to escape
   * @returns {string}
   */
  static escapeHtml(text) {
    if (typeof text !== 'string') return text;
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * Linkify text (convert URLs to links)
   * @param {string} text - Text to linkify
   * @returns {string}
   */
  static linkify(text) {
    if (typeof text !== 'string') return text;
    
    const urlPattern = /(\b(https?|ftp):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/gim;
    const wwwPattern = /(^|[^\/])(www\.[\S]+(\b|$))/gim;
    
    let linkedText = text.replace(
      urlPattern,
      "<a href='$1' target='_blank' rel='noopener noreferrer'>$1</a>"
    );
    
    linkedText = linkedText.replace(
      wwwPattern,
      "$1<a href='http://$2' target='_blank' rel='noopener noreferrer'>$2</a>"
    );
    
    return linkedText;
  }

  /**
   * Clip text to max length with ellipsis
   * @param {string} text - Text to clip
   * @param {number} maxLength - Maximum length
   * @returns {string}
   */
  static clipText(text, maxLength = 45) {
    if (typeof text !== 'string') return text;
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  }

  /**
   * Format date string
   * @param {Date|string|number} date - Date to format
   * @param {string} format - Format string (YYYY-MM-DD, etc.)
   * @returns {string}
   */
  static formatDate(date, format = 'YYYY-MM-DD') {
    if (!date) return '';
    
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return '';

    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');

    return format
      .replace('YYYY', year)
      .replace('MM', month)
      .replace('DD', day)
      .replace('HH', hours)
      .replace('mm', minutes)
      .replace('ss', seconds);
  }

  /**
   * Get today's date in YYYY-MM-DD format
   * @returns {string}
   */
  static getTodayDate() {
    return this.formatDate(new Date(), 'YYYY-MM-DD');
  }

  /**
   * Sanitize input value
   * @param {string} value - Value to sanitize
   * @returns {string}
   */
  static sanitize(value) {
    if (typeof value !== 'string') return value;
    return value.trim().replace(/[<>]/g, '');
  }

  /**
   * Format number with decimal places
   * @param {number} value - Number to format
   * @param {number} decimals - Number of decimal places
   * @returns {string}
   */
  static formatNumber(value, decimals = 0) {
    if (isNaN(value)) return '';
    return parseFloat(value).toFixed(decimals);
  }
}

